/**
 * @fileoverview Syndicator Showdown — BUY Modal (Equity Model v2)
 * @module components/BuyModal
 *
 * ─── EQUITY MODEL ─────────────────────────────────────────────────────────
 * Tracks TWO values at all times:
 *   currentOwnedPct — what the player already owns in this runner (from
 *                     EntryContext allocations; 0 if none)
 *   draftTargetPct  — what the modal's selector currently shows (starts at
 *                     currentOwnedPct, adjustable via arrows/quick-buy)
 *
 * deltaPct  = draftTargetPct - currentOwnedPct   (the ADDITIONAL equity bought)
 * deltaCost = cost(draftTargetPct) - cost(currentOwnedPct)  (ADDITIONAL charge)
 *
 * Selling (draftTargetPct < currentOwnedPct) is intentionally unsupported
 * here — that flow lives in SellEquityModal. The arrows/quick-buy buttons
 * therefore never move draftTargetPct below currentOwnedPct.
 *
 * onConfirm still receives the FULL target equity % and FULL total cost
 * (matching EntryContext.updateRunnerEquity's existing contract) — only the
 * on-screen framing changed to "current vs draft", not the storage shape.
 *
 * ─── COST CALCULATION ─────────────────────────────────────────────────────
 * All costs go through calculatePurchaseCost (@ss/shared) via
 * utils/equityPricing.js — the tiered T1..T5 curve, single source of truth
 * shared with ReviewSlip/RaceCard/the compliance engine. Quick-buy button
 * targets are solved against this same curve (not a linear equity*price
 * shortcut), so "Race Floor" etc. always produce a cost that actually meets
 * the stated target.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MAX_FIELD_COVERAGE } from '@ss/shared/constants';
import { getValueBadgeMeta } from '../utils/valueBadge';
import {
  costForEquity,
  calcRaceFloorTarget,
  calcRaceAvgTarget,
  calcMaxTarget,
  calcMaxSafeTarget,
} from '../utils/equityPricing';
import './BuyModal.css';

// ─── Donut chart ──────────────────────────────────────────────────────────────

const DONUT_SIZE   = 128;
const STROKE_WIDTH = 10;
const RADIUS       = (DONUT_SIZE - STROKE_WIDTH * 2) / 2;
const CX = DONUT_SIZE / 2;
const CY = DONUT_SIZE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const DonutChart = ({ equity, maxEquity, color }) => {
  const progress    = maxEquity > 0 ? Math.min(equity / maxEquity, 1) : 0;
  const dashoffset  = CIRCUMFERENCE * (1 - progress);

  return (
    <svg width={DONUT_SIZE} height={DONUT_SIZE} viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} aria-hidden="true">
      <circle cx={CX} cy={CY} r={RADIUS} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={STROKE_WIDTH} />
      <circle
        cx={CX} cy={CY} r={RADIUS}
        fill="none"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={`${CIRCUMFERENCE} ${CIRCUMFERENCE}`}
        strokeDashoffset={dashoffset}
        transform={`rotate(-90 ${CX} ${CY})`}
        style={{ transition: 'stroke-dashoffset 0.15s ease, stroke 0.25s ease' }}
      />
      <text x={CX} y={CY - 6} textAnchor="middle" fill="white" fontSize="24" fontWeight="700" fontFamily="var(--font-data, monospace)">
        {equity}
      </text>
      <text x={CX} y={CY + 14} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="10" fontFamily="var(--font-ui, sans-serif)">
        % equity
      </text>
    </svg>
  );
};

// Colour accent for the donut arc only — matches the tiered pricing bands.
function getBandColor(equity) {
  if (equity === 0)  return 'var(--border-medium)';
  if (equity <= 30)  return 'var(--accent-green)';
  if (equity <= 55)  return 'var(--accent-blue)';
  if (equity <= 75)  return 'var(--accent-amber)';
  if (equity <= 90)  return '#f97316';
  return 'var(--accent-red)';
}

// ─── Value badge (mirrors RunnerRow's ValuePill) ───────────────────────────────

const ValueBadge = ({ valueBadge, valueBadgeMeta }) => {
  if (!valueBadge || valueBadge === 'UNKNOWN') return null;
  const meta = getValueBadgeMeta(valueBadge, valueBadgeMeta);
  return (
    <span className="ss-buy-value-badge" style={{ borderColor: meta.colour, color: meta.colour, background: meta.bg }} title={meta.label}>
      {meta.label}
    </span>
  );
};

// ─── Stepper button (press-and-hold repeat) ────────────────────────────────────

const StepButton = ({ direction, onChange, disabled, symbol }) => {
  const timerRef    = useRef(null);
  const intervalRef = useRef(null);
  const speedRef    = useRef(1);
  const accelRef    = useRef(null);

  const start = useCallback(() => {
    if (disabled) return;
    speedRef.current = 1;
    onChange(direction);

    timerRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        onChange(direction * speedRef.current);
      }, 100);
      accelRef.current = setTimeout(() => { speedRef.current = 5; }, 1100);
    }, 400);
  }, [direction, onChange, disabled]);

  const stop = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(intervalRef.current);
    clearTimeout(accelRef.current);
    speedRef.current = 1;
  }, []);

  useEffect(() => stop, [stop]);

  return (
    <button
      className={`buy-modal__adj-btn ${disabled ? 'buy-modal__adj-btn--disabled' : ''}`}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={(e) => { e.preventDefault(); start(); }}
      onTouchEnd={(e)   => { e.preventDefault(); stop();  }}
      type="button"
      aria-label={direction > 0 ? 'Increase target equity' : 'Decrease target equity'}
      disabled={disabled}
    >
      {symbol}
    </button>
  );
};

// ─── Main BuyModal ────────────────────────────────────────────────────────────

/**
 * @param {boolean}  isOpen
 * @param {Function} onClose
 * @param {Function} onConfirm(targetEquityPct, totalCostAtTargetEquity) — called on Confirm
 * @param {Object}   runner
 * @param {string}   raceId
 * @param {number}   raceNumber
 * @param {string}   [raceName]
 * @param {number}   baseSharePrice        — cost of 1% equity (from calculateBaseSharePrice)
 * @param {number}   maxOwnershipPct       — per-runner equity ceiling (normally 100)
 * @param {number}   currentEquity         — currentOwnedPct — what the player already holds (0 if none)
 * @param {number}   currentRaceSpend      — total already spent across ALL runners in this race
 * @param {number}   avgSpendPerRace       — slate's average target spend per race
 * @param {number}   perRaceFloor          — minimum spend required in this race
 * @param {number}   remainingBudget       — player's wallet-wide remaining virtual dollars
 * @param {number}   [reservedForFloors]   — sum of floor shortfalls across OTHER races (for Max Safe)
 * @param {number}   runnersCurrentlyHeld  — distinct runners the player holds in this race
 * @param {number}   activeRunnerCount     — total active (non-scratched) runners in this race
 * @param {Object}   [gamePrizePool]       — resolved GAME prize pool { first, second, third, fourth, fifth }
 * @param {boolean}  [isRaceLocked]
 */
export const BuyModal = ({
  isOpen,
  onClose,
  onConfirm,
  runner,
  raceId,
  raceNumber,
  raceName,
  baseSharePrice,
  maxOwnershipPct      = 100,
  currentEquity        = 0,
  currentRaceSpend     = 0,
  avgSpendPerRace      = 100_000,
  perRaceFloor         = 0,
  remainingBudget      = 0,
  reservedForFloors    = 0,
  runnersCurrentlyHeld = 0,
  activeRunnerCount    = 1,
  gamePrizePool        = null,
  isRaceLocked         = false,
}) => {
  const currentOwnedPct = currentEquity;
  const maxPct = Math.min(100, maxOwnershipPct || 100);

  const [draftTargetPct, setDraftTargetPct] = useState(currentOwnedPct);

  // Reset whenever the modal opens for a (potentially different) runner
  useEffect(() => {
    if (isOpen) setDraftTargetPct(currentOwnedPct);
  }, [isOpen, runner?.id, currentOwnedPct]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ESC closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Cost calculations ──────────────────────────────────────────────────────

  const costAtCurrent = useMemo(() => costForEquity(baseSharePrice, currentOwnedPct), [baseSharePrice, currentOwnedPct]);
  const costAtDraft   = useMemo(() => costForEquity(baseSharePrice, draftTargetPct),   [baseSharePrice, draftTargetPct]);

  const deltaPct  = draftTargetPct - currentOwnedPct;
  const deltaCost = costAtDraft - costAtCurrent;

  // ── Compliance ─────────────────────────────────────────────────────────────

  const isNewRunner       = currentOwnedPct === 0 && draftTargetPct > 0;
  const maxRunnersAllowed = Math.max(1, Math.floor(activeRunnerCount * MAX_FIELD_COVERAGE));
  const atFieldCap        = runnersCurrentlyHeld >= maxRunnersAllowed;
  const blockedByFieldCap = isNewRunner && atFieldCap;
  const overBudget        = deltaCost > remainingBudget;

  const canConfirm = !isRaceLocked && !blockedByFieldCap && !overBudget && deltaPct > 0;

  // ── Race summary (before → after) ───────────────────────────────────────────

  const projectedRaceSpend  = currentRaceSpend + deltaCost;
  const meetsFloorAfter     = projectedRaceSpend >= perRaceFloor;
  const projectedRunnerCount = runnersCurrentlyHeld + (isNewRunner && deltaPct > 0 ? 1 : 0);
  const projectedWallet     = remainingBudget - deltaCost;

  // ── Arrow stepper ───────────────────────────────────────────────────────────

  const handleStep = useCallback((delta) => {
    setDraftTargetPct(v => Math.min(maxPct, Math.max(currentOwnedPct, v + delta)));
  }, [maxPct, currentOwnedPct]);

  // ── Quick-buy targets (pure calculators live in utils/equityPricing.js —
  //    unit-tested there for the "accounts for other runners' spend"
  //    behaviour so it doesn't need a rendered component to verify) ──────────

  const handleRaceFloor = useCallback(() => {
    setDraftTargetPct(calcRaceFloorTarget({
      basePrice: baseSharePrice, currentOwnedPct, currentRaceSpend, raceFloor: perRaceFloor, maxPct,
    }));
  }, [baseSharePrice, currentOwnedPct, currentRaceSpend, perRaceFloor, maxPct]);

  const handleRaceAvg = useCallback(() => {
    setDraftTargetPct(calcRaceAvgTarget({
      basePrice: baseSharePrice, currentOwnedPct, currentRaceSpend, avgSpendPerRace, maxPct,
    }));
  }, [baseSharePrice, currentOwnedPct, currentRaceSpend, avgSpendPerRace, maxPct]);

  const handleMaxTarget = useCallback(() => {
    setDraftTargetPct(calcMaxTarget({
      basePrice: baseSharePrice, currentOwnedPct, walletRemaining: remainingBudget, maxPct,
    }));
  }, [baseSharePrice, currentOwnedPct, remainingBudget, maxPct]);

  const handleMaxSafe = useCallback(() => {
    setDraftTargetPct(calcMaxSafeTarget({
      basePrice: baseSharePrice, currentOwnedPct, walletRemaining: remainingBudget, reservedForFloors, maxPct,
    }));
  }, [baseSharePrice, currentOwnedPct, remainingBudget, reservedForFloors, maxPct]);

  // Description shown under the quick-buy row — updates on hover/focus so
  // it's clear these buttons set a TARGET equity, not "buy this much more".
  const [quickBtnHint, setQuickBtnHint] = useState(null);
  const QUICK_BTN_HELP = {
    floor:   'Top up this race to the required minimum.',
    avg:     'Buy enough to move this race toward average spend.',
    max:     'Buy the maximum available equity in this runner.',
    safe:    'Use the maximum safe wallet amount while reserving future race floors.',
  };

  if (!isOpen || !runner) return null;

  const formatCurrency = (v) => '$' + Math.round(v || 0).toLocaleString('en-AU');
  const band = getBandColor(draftTargetPct);

  // Rendered via portal directly under <body> — this escapes any ancestor
  // overflow:hidden / transform / filter / backdrop-filter that would
  // otherwise clip or reposition a fixed-position modal nested inside
  // RaceCard/SelectionPanel/AppShell's scrolling containers.
  return createPortal(
    <div className="buy-modal__overlay" onClick={onClose} role="presentation">
      <div
        className="buy-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={`Buy equity in ${runner.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="buy-modal__close" onClick={onClose} type="button" aria-label="Close buy modal">✕</button>

        {/* ── Header (full width) ── */}
        <div className="ss-buy-modal-header">
          <div className="ss-buy-runner-id">
            <span className="ss-buy-runner-num">{runner.runnerNumber}</span>
            <div>
              <h2 className="ss-buy-runner-name">{runner.name}</h2>
              <span className="ss-buy-leg-label">
                Leg {raceNumber}{raceName ? ` · ${raceName}` : ''}
              </span>
            </div>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="buy-modal__scroll-body">
          <div className="buy-modal__body-grid">

            {/* ── Left column: runner info + equity controls ── */}
            <div className="buy-modal__col-left">

              {/* Runner info strip */}
              <div className="ss-buy-runner-stats">
                {(runner.displayRating != null || (runner.valueBadge && runner.valueBadge !== 'UNKNOWN')) && (
                  <div className="ss-buy-stat-row">
                    <span>Rating / Value</span>
                    <strong className="ss-buy-rating-value-cell">
                      {runner.displayRating != null && <span>{Math.round(runner.displayRating)}</span>}
                      <ValueBadge valueBadge={runner.valueBadge} valueBadgeMeta={runner.valueBadgeMeta} />
                    </strong>
                  </div>
                )}
                <div className="ss-buy-stat-row">
                  <span>Connections</span>
                  <strong>
                    {runner.barrier ? `B${runner.barrier}` : ''}
                    {runner.jockey ? ` · ${runner.jockey}` : ''}
                    {runner.trainer ? ` · ${runner.trainer}` : ''}
                  </strong>
                </div>
                <div className="ss-buy-stat-row">
                  <span>Odds · Price/1%</span>
                  <strong>${(runner.odds ?? 0).toFixed(2)} · {formatCurrency(baseSharePrice)}</strong>
                </div>
              </div>

              {runner.valueExplanation && (
                <div className="ss-buy-value-explanation">{runner.valueExplanation}</div>
              )}

              {/* Donut + target equity readout */}
              <div className="buy-modal__target-display">
                <DonutChart equity={draftTargetPct} maxEquity={maxPct} color={band} />
                <div className="buy-modal__target-text">
                  <span className="buy-modal__target-label">Target Equity</span>
                  <span className="buy-modal__target-pct">{draftTargetPct}%</span>
                  <span className="buy-modal__target-delta">
                    {deltaPct > 0
                      ? `+${deltaPct}% (${formatCurrency(deltaCost)})`
                      : 'No change'}
                  </span>
                </div>
              </div>

              {/* Arrow controls */}
              <div className="buy-modal__arrow-row">
                <StepButton direction={-1} symbol="−" onChange={handleStep} disabled={draftTargetPct <= currentOwnedPct} />
                <div className="buy-modal__arrow-readout">
                  <span className="buy-modal__arrow-pct">{draftTargetPct}%</span>
                  <span className="buy-modal__arrow-delta">
                    {deltaPct > 0 ? `+${deltaPct}% (${formatCurrency(deltaCost)})` : 'No change'}
                  </span>
                </div>
                <StepButton direction={1} symbol="+" onChange={handleStep} disabled={draftTargetPct >= maxPct} />
              </div>

              {/* Quick-buy buttons — set a TARGET equity, not "buy this much more" */}
              <div className="buy-modal__quick-btns">
                <button
                  type="button"
                  className="buy-modal__quick-btn"
                  onClick={handleRaceFloor}
                  disabled={isRaceLocked}
                  title={QUICK_BTN_HELP.floor}
                  onMouseEnter={() => setQuickBtnHint(QUICK_BTN_HELP.floor)}
                  onMouseLeave={() => setQuickBtnHint(null)}
                  onFocus={() => setQuickBtnHint(QUICK_BTN_HELP.floor)}
                  onBlur={() => setQuickBtnHint(null)}
                >
                  To Floor
                </button>
                <button
                  type="button"
                  className="buy-modal__quick-btn"
                  onClick={handleRaceAvg}
                  disabled={isRaceLocked}
                  title={QUICK_BTN_HELP.avg}
                  onMouseEnter={() => setQuickBtnHint(QUICK_BTN_HELP.avg)}
                  onMouseLeave={() => setQuickBtnHint(null)}
                  onFocus={() => setQuickBtnHint(QUICK_BTN_HELP.avg)}
                  onBlur={() => setQuickBtnHint(null)}
                >
                  To Avg
                </button>
                <button
                  type="button"
                  className="buy-modal__quick-btn"
                  onClick={handleMaxTarget}
                  disabled={isRaceLocked}
                  title={QUICK_BTN_HELP.max}
                  onMouseEnter={() => setQuickBtnHint(QUICK_BTN_HELP.max)}
                  onMouseLeave={() => setQuickBtnHint(null)}
                  onFocus={() => setQuickBtnHint(QUICK_BTN_HELP.max)}
                  onBlur={() => setQuickBtnHint(null)}
                >
                  100%
                </button>
                <button
                  type="button"
                  className="buy-modal__quick-btn"
                  onClick={handleMaxSafe}
                  disabled={isRaceLocked}
                  title={QUICK_BTN_HELP.safe}
                  onMouseEnter={() => setQuickBtnHint(QUICK_BTN_HELP.safe)}
                  onMouseLeave={() => setQuickBtnHint(null)}
                  onFocus={() => setQuickBtnHint(QUICK_BTN_HELP.safe)}
                  onBlur={() => setQuickBtnHint(null)}
                >
                  Max Safe
                </button>
              </div>
              <div className="buy-modal__quick-btns-hint">
                {quickBtnHint || 'Max Safe reserves enough for remaining race floors'}
              </div>
            </div>

            {/* ── Right column: race summary + payouts + warnings ── */}
            <div className="buy-modal__col-right">

              {/* Race summary — before → after */}
              <div className="buy-modal__summary">
                <div className="buy-modal__summary-row">
                  <span className="buy-modal__summary-label">Spent this race</span>
                  <span className="buy-modal__summary-value">
                    {formatCurrency(currentRaceSpend)}
                    {deltaCost > 0 && (
                      <span className="buy-modal__summary-arrow"> → {formatCurrency(projectedRaceSpend)}</span>
                    )}
                    <span className="buy-modal__summary-sub"> / {formatCurrency(avgSpendPerRace)} avg</span>
                  </span>
                </div>

                <div className="buy-modal__summary-row">
                  <span className="buy-modal__summary-label">Race floor</span>
                  <span className={`buy-modal__summary-value ${meetsFloorAfter ? 'text-green' : 'text-amber'}`}>
                    {meetsFloorAfter ? '✅' : '⚠️'} {formatCurrency(projectedRaceSpend)} / {formatCurrency(perRaceFloor)}
                  </span>
                </div>

                <div className="buy-modal__summary-row">
                  <span className="buy-modal__summary-label">Runners this race</span>
                  <span className="buy-modal__summary-value">
                    {runnersCurrentlyHeld}{projectedRunnerCount !== runnersCurrentlyHeld ? ` → ${projectedRunnerCount}` : ''} / {maxRunnersAllowed} max
                  </span>
                </div>

                <div className="buy-modal__summary-row">
                  <span className="buy-modal__summary-label">Wallet</span>
                  <span className="buy-modal__summary-value">
                    {formatCurrency(remainingBudget)}
                    {deltaCost > 0 && (
                      <span className="buy-modal__summary-arrow"> → {formatCurrency(projectedWallet)}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Projected payouts */}
              {gamePrizePool && (
                <div className="buy-modal__payouts">
                  <div className="buy-modal__payouts-title">Projected Payouts</div>
                  {[
                    { place: '1st', key: 'first' },
                    { place: '2nd', key: 'second' },
                    { place: '3rd', key: 'third' },
                    { place: '4th', key: 'fourth' },
                    { place: '5th', key: 'fifth' },
                  ].map(({ place, key }) => {
                    const prizeForPlace = gamePrizePool?.[key] || 0;
                    if (!prizeForPlace) return null;
                    const playerPayout = Math.round((draftTargetPct / 100) * prizeForPlace);
                    return (
                      <div key={place} className="buy-modal__payout-row">
                        <span className="buy-modal__payout-place">{place}</span>
                        <span className="buy-modal__payout-calc">
                          {draftTargetPct}% × {formatCurrency(prizeForPlace)}
                        </span>
                        <span className={`buy-modal__payout-value ${place === '1st' ? 'text-gold' : ''}`}>
                          = {formatCurrency(playerPayout)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Warnings */}
              {isRaceLocked && (
                <div className="ss-buy-warning">🔒 This race has locked — no buying allowed after jump time.</div>
              )}
              {!isRaceLocked && blockedByFieldCap && (
                <div className="ss-buy-warning">
                  Field coverage cap reached — max {maxRunnersAllowed} runners per race ({Math.round(MAX_FIELD_COVERAGE * 100)}%
                  field coverage). Sell out of an existing runner first if you want to add a new one.
                </div>
              )}
              {!isRaceLocked && !blockedByFieldCap && overBudget && (
                <div className="ss-buy-warning">⚠ Exceeds remaining wallet balance</div>
              )}
            </div>

          </div>
        </div>

        {/* ── Sticky confirm section (full width) ── */}
        <div className="buy-modal__confirm-section">
          <button
            className={`ss-buy-confirm-btn ${canConfirm ? 'ss-buy-confirm-btn--active' : 'ss-buy-confirm-btn--disabled'}`}
            onClick={() => canConfirm && onConfirm(draftTargetPct, costAtDraft)}
            disabled={!canConfirm}
            type="button"
          >
            {deltaPct > 0
              ? `Confirm Purchase — ${formatCurrency(deltaCost)}`
              : 'Adjust equity above to buy'}
          </button>
          <div className="buy-modal__remaining">
            {deltaPct > 0
              ? `Remaining after: ${formatCurrency(projectedWallet)}`
              : `Current wallet: ${formatCurrency(remainingBudget)}`}
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default BuyModal;
